import {
  Typography,
  CardMedia,
  Tooltip,
  Box,
  Divider,
} from "@mui/material";
import Stack from "@mui/material/Stack";

import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";

import { useDispatch } from "react-redux";
import {
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setOthersNeeded,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { logEvent } from "services/amplitude.service.js";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { showNewsguard } from "utils/show-ratings";
import OpenAccessChip from "../open-access-chip";
import SmileyIcon from "features/News/components/smiley-icon";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export const CategoryCard = ({ data = {}, cardRef, category, query }) => {
  const [isHovered, setIsHovered] = useState(false);

  const dispatch = useDispatch();

  // const imageUrl = data?.openGraphImage?.contentUrl
  //   ? data?.openGraphImage?.contentUrl
  //   : defaultBackground;

  const openSourceSidebar = () => {
    logEvent("click_card_header", {
      host: data.hostName,
      entity: data.entityName,
    });
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const newUrl = (() => {
    let updatedUrl = data.url;

    if (updatedUrl?.includes("foreignpolicy.com")) {
      updatedUrl += "?auth=sooth";
    }

    return updatedUrl;
  })();

  const openSidebar = () => {
    logEvent("open_bookmark_popup", {
      url: newUrl,
      query: query,
      entity: data.entityName,
      category: category,
      type: "individual-category",
      mediabiasrating: data.mediaBiasRating ? data.mediaBiasRating : "NA",
      score: showNewsguard(data.score) ? data.score : "NA",
    });
    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    setIsHovered(false);
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.name,
        published_date: data.datePublishedDisplayText,
        url: data.url,
        utc_date_published: data.datePublished,
      })
    );
    dispatch(setCollectionId(data.collectionId));
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          openSidebar();
          setIsHovered(false);
        }}
        sx={{
          cursor: "pointer",
          pl: 1,
          pr: 1,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
      >
        {data.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              width: 17,
              height: 17,
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  width: 17,
                  height: 17,
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          title="Open Bookmark"
          onClick={openSidebar}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const ImageSection = () => {
    const imageUrl = data?.openGraphImage?.contentUrl;

    const [errored, setErrored] = useState(false);

    function onError({ currentTarget }) {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    }
    return (
      <Box>
        <CardMedia
          component="img"
          sx={{
            aspectRatio: 1,
            borderRadius: "10px",
          }}
          image={imageUrl == null ? defaultBackground : imageUrl}
          alt={imageUrl == null ? "Image not found" : data?.name}
          onError={onError}
        />
      </Box>
    );
  };

  return (
    <Box
      ref={cardRef}
      // container
      spacing={2}
      sx={{
        display: "grid",
        gridTemplateAreas: `
      'sourceSidebar sourceSidebar sourceSidebar'
      'content content content'
      'divider divider divider'`,
        gridTemplateColumns: "3fr 0.2fr 1fr",
        gridTemplateRows: "auto 1fr auto",
        padding: 0,
        mb: 1,
      }}
    >
      {/* Sidebar Section */}
      <Box
        // item
        sx={{
          gridArea: "sourceSidebar",
          mb: 2,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "secondary.main" },
          borderRadius: 1,
          py: 0.4,
          px: 1,
        }}
        onClick={openSourceSidebar}
      >
        <Typography
          variant="body1"
          sx={{ fontWeight: "bold", color: "grey.600" }}
        >
          {data.entityName}
        </Typography>

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            width: { md: "12vw", lg: "10vw" },
            minWidth: "160px",
          }}
        >
          {data.hasOpenAccess && <OpenAccessChip />}
          {data.mediaBiasRating && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: { md: "8vw", lg: "7vw" },
                minWidth: "80px",
                ml: 1,
              }}
            >
              <AllSidesRating activeCategory={data.mediaBiasRating} />
            </Box>
          )}
          {showNewsguard(data.score) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: { md: "5vw", lg: "3vw" },
                minWidth: "50px",
              }}
            >
              <NewsguardScore score={Number(data.score)} />
            </Box>
          )}

          {data.charityNavigatorScore &&
            category !== "Academia & Scholarly Resources" &&
            !data.score &&
            !data.mediaBiasRating && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: { md: "5vw", lg: "3vw" },
                  minWidth: "50px",
                }}
              >
                <CharityNavigatorScore
                  score={data.charityNavigatorScore}
                  size="small"
                />
              </Box>
            )}
        </Box>
      </Box>

      {/* Content Section: Title, Description, and Image */}
      <Box
        // item
        sx={{
          gridArea: "content",
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          pl: 1,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
            }}
          >
            <SearchResultLink
              url={data.url}
              trackingCat="click_resultset_title"
              trackingOptions={{
                url: data.url,
                query: query,
                entity: data.entityName,
                category: category,
                mediabiasrating: data.mediaBiasRating
                  ? data.mediaBiasRating
                  : "NA",
                score: showNewsguard(data.score) ? data.score : "NA",
                title: data.name,
                date_published: data.datePublished,
              }}
              variant="body4"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.5em",
                maxWidth: "90%",
                flexShrink: 0,
                cursor: "pointer",
                "&:hover": {
                  color: "rgb(18,89,151)",
                },
              }}
            >
              {data.name}
            </SearchResultLink>
            {data.hasPayWall && data.isPartner ? (
              <SmileyIcon />
            ) : (
              data.hasPayWall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer", mt: "0.2em" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>

          {/* Description and Bookmark Options in a Row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: { xs: 3, sm: 5 },
                WebkitBoxOrient: "vertical",
                maxHeight: { xs: "6vh", sm: "10vh" },
                lineHeight: { xs: "2vh", sm: "2vh" },
                width: "90%",
              }}
            >
              {data.datePublishedDisplayText &&
                `${data.datePublishedDisplayText} - `}
              {data.snippet}
            </Typography>

            {/* Bookmark Options */}
          </Stack>
        </Box>

        {/* Image Section */}
        <Box
          // item
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
            width: "10vw",
            pr: 1,
          }}
        >
          <ImageSection />
          <Box sx={{ mt: 1 }}>
            <BookmarkOptions />
          </Box>
        </Box>
      </Box>

      {/* Divider Section */}
      <Box
        // item
        sx={{
          gridArea: "divider",
          mt: 1,
          paddingLeft: 0,
        }}
      >
        <Divider />
      </Box>
    </Box>
  );
};
